



import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth } from '../useAuth';
import Logout from '../../buttons/Logout';
import { setScreen } from '../../slices/screenState';
import styles from './menubar.module.css';

export const MenuBar = () => {
    const dispatch = useDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    const { firstName } = useSelector(state => state.tempSettings.data);
    const messages = useSelector((state) => state.messages.data);
    const currentScreen = useSelector((state) => state.screenState);
    const showMessageIndicator = messages.length;

    // Automations dot. Same runtime state the Rules screen renders as
    // Good / Warn / Alert badges (see Rules/index.js) -- read from the
    // canonical rules list, not tempRules, so an open editor doesn't
    // change what the menubar shows. Fired outranks warning.
    const ruleAlertLevel = useSelector((state) => {
        const list = state.rules.list || [];
        if (list.some(r => r._alert_fired)) return 'fired';
        if (list.some(r => r._on_alert))    return 'warn';
        return null;
    });

    // Until subscribed, every screen but Settings is locked. Matches the
    // server allowlist ('subscribed'); the back-end gate is the real boundary.
    const isSubscribed = useSelector(state => state.settings.data.subscriptionStatus === 'subscribed');

    // A screen with an open editor / edit mode locks ALL navigation
    // (including Settings): finish with Done, Save, or Cancel first.
    const editLocked = useSelector(state => state.editLock);

    const navLocked = !isSubscribed || editLocked;

    // Collapsed hamburger has to stand in for every buried indicator,
    // otherwise a fired alert is invisible on mobile until the menu opens.
    const hamburgerLevel =
        (showMessageIndicator || ruleAlertLevel === 'fired') ? 'fired'
      : (ruleAlertLevel === 'warn')                          ? 'warn'
      : null;

    const handleButtonClick = (screen) => {
        setMenuOpen(false);
        dispatch(setScreen(screen));
    };

    const btnClass = (screen) => currentScreen === screen ? 'selected' : '';

    const alertDot = ruleAlertLevel
        ? <div className={ruleAlertLevel === 'fired' ? styles.alertIndicator : styles.warnIndicator}></div>
        : null;

    useEffect(() => {
        if (!menuOpen) return;
        const close = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('pointerdown', close);
        return () => document.removeEventListener('pointerdown', close);
    }, [menuOpen]);

    useAuth();

    return (
        <>
            <div id="horizontal" className={styles.menubar}>
                <div className={styles.left}>
                    <img className={styles.logo} src="/graphics/logo_sm.jpg" alt="OhioIoT" />
                    <div>Hello, {firstName}</div>
                    <Logout />
                </div>

                <div className={styles.right}>
                    
                    <button disabled={navLocked} className={btnClass('data')} onClick={() => handleButtonClick('data')}>Data</button>
                    <button disabled={navLocked} className={btnClass('devices')} onClick={() => handleButtonClick('devices')}>Devices</button>
                    <button disabled={navLocked} className={btnClass('controls')} onClick={() => handleButtonClick('controls')}>Controls</button>
                    <div className={styles.automationsButton}>
                        <button disabled={navLocked} className={btnClass('automations')} onClick={() => handleButtonClick('automations')}>Automations</button>
                        {alertDot}
                    </div>
                    <button disabled={navLocked} className={btnClass('ota')} onClick={() => handleButtonClick('ota')}>OTA</button>
                    <div className={styles.messagesButton}>
                        <button disabled={navLocked} className={btnClass('messages')} onClick={() => handleButtonClick('messages')}>Messages</button>
                        {showMessageIndicator ? <div className={styles.messageIndicator}></div> : null}
                    </div>
                    <button disabled={editLocked} className={btnClass('settings')} onClick={() => handleButtonClick('settings')}>Settings</button>
                </div>

                <div className={styles.hamburgerWrapper} ref={menuRef}>
                    <button
                        className={styles.hamburger}
                        onClick={() => setMenuOpen(v => !v)}
                        aria-label="Open menu"
                    >
                        ☰
                    </button>
                    {hamburgerLevel && !menuOpen ? (
                        <div className={hamburgerLevel === 'fired' ? styles.hamburgerIndicator : styles.hamburgerIndicatorWarn}></div>
                    ) : null}

                    {menuOpen && (
                        <div className={styles.mobileMenu}>
                            <div className={styles.mobileMenuButtons}>
                                <button disabled={navLocked} className={btnClass('data')} onClick={() => handleButtonClick('data')}>Data</button>
                                <button disabled={navLocked} className={btnClass('devices')} onClick={() => handleButtonClick('devices')}>Devices</button>
                                <button disabled={navLocked} className={btnClass('controls')} onClick={() => handleButtonClick('controls')}>Controls</button>
                                <div className={styles.mobileAutomationsButton}>
                                    <button disabled={navLocked} className={btnClass('automations')} onClick={() => handleButtonClick('automations')}>Automations</button>
                                    {alertDot}
                                </div>
                                <button disabled={navLocked} className={btnClass('ota')} onClick={() => handleButtonClick('ota')}>OTA</button>
                                <div className={styles.mobileMessagesButton}>
                                    <button disabled={navLocked} className={btnClass('messages')} onClick={() => handleButtonClick('messages')}>Messages</button>
                                    {showMessageIndicator ? <div className={styles.messageIndicator}></div> : null}
                                </div>
                                <button disabled={editLocked} className={btnClass('settings')} onClick={() => handleButtonClick('settings')}>Settings</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};